<script setup lang="ts">
import SubjectArea from '@/components/SubjectArea.vue';
import VideoThumbnail from '@/components/videos/VideoThumbnail.vue';
import { useCategories } from '@ilteducation/content-ref-data';
import { computed, watch } from 'vue';
import { useLocale } from '@/plugins/translation';
import { storeToRefs } from 'pinia';
import type { Area } from '@ilteducation/content-ref-data-types';
import CategoryBubble from '@/components/CategoryBubble.vue';
import { Video } from '@ilteducation/videos';
import { useSearchContexts } from '@ilteducation/content-search';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

type VideoWithSubject = Video & { subject: string };

const props = withDefaults(
  defineProps<{
    selectedSubject: string;
    videos: Video[];
    areaGuides: Area[];
    contextId: string;
  }>(),
  {
    selectedSubject: '',
  },
);

const { t, l } = useLocale();
const { byId: getCategoryById } = useCategories();
const searchStore = useSearchContexts('Video');
const { next, trackResultClick } = searchStore;
const { isLoading, hasMore } = storeToRefs(searchStore);
const firstPageResults = computed(() => props.videos.length === 0);
const hasMoreVideos = computed(() => !isLoading.value(props.contextId) && hasMore.value(props.contextId));

const currentSubject = computed(() => getCategoryById.value(props.selectedSubject));

watch(
  () => hasMoreVideos.value,
  () => {
    if (currentSubject.value && hasMoreVideos.value) {
      // Automatically load more videos to fill the subject area if there are more videos available
      next(props.contextId);
    }
  },
);

const sortedVideos = computed(() => props.videos.flatMap((v) => v.categories.map((cat) => ({ ...v, subject: cat }))));

const getVideoKey = (video: VideoWithSubject) => {
  return `${video.id}-${video.subject}`;
};

const onVideoClicked = (clickedVideo: VideoWithSubject): void => {
  const videoPositionInSearchResults =
    sortedVideos.value.findIndex((video) => getVideoKey(video) === getVideoKey(clickedVideo)) + 1; // We want natural index

  trackResultClick(props.contextId, getVideoKey(clickedVideo), videoPositionInSearchResults);
};
</script>

<template>
  <v-container class="search-results__container bg-surface-container position-relative">
    <v-row>
      <v-col>
        <v-row>
          <v-col class="d-flex align-center">
            <v-progress-circular
              v-if="firstPageResults && (contextId === 'transition' || isLoading(contextId))"
              class="ml-2"
              indeterminate
              size="30"
              color="primary"
              :aria-label="t(l.label_loading_search_results)"
            />
            <span v-else class="search-results-caption">{{
              videos.length + areaGuides.length === 0 ? t(l.search.no_video_search_result) : t(l.search.search_result)
            }}</span>
          </v-col>
        </v-row>
        <v-row v-if="selectedSubject">
          <v-col>
            <SubjectArea :subject="currentSubject" :subject-videos="videos" :is-search-results="true" />
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col v-for="area in areaGuides" :key="area.name" cols="6" sm="4" md="3">
            <router-link
              :to="{
                name: 'videos.AreaGuide',
                params: {
                  subject: getCategoryById(area.categoryId)?.slug,
                  area: area.slug,
                },
              }"
              class="text-decoration-none"
            >
              <v-card rounded="0" class="area-guide-result mb-3" :color="getCategoryById(area.categoryId)?.color">
                <CategoryBubble
                  v-if="getCategoryById(area.categoryId)?.categoryCode"
                  class="category-bubble"
                  :subject="getCategoryById(area.categoryId)!"
                  size="small"
                />
                <h4>{{ area.name }}</h4>
              </v-card>
              <div class="mt-3 text-secondary area-guide-result--subtitle">
                {{ t(l.area_guide.go_to) }}: {{ area.name }}
              </div>
            </router-link>
          </v-col>
          <VideoThumbnail
            v-for="video in sortedVideos"
            :key="getVideoKey(video)"
            :video="video"
            :subject-id="video.subject"
            :use-theme-styling="false"
            @click="() => onVideoClicked(video)"
          />
          <v-col cols="12" class="d-flex justify-center">
            <v-btn
              v-if="hasMore(contextId) && !isLoading(contextId)"
              class="text-decoration-none text-none"
              variant="text"
              color="primary"
              @click="next(contextId)"
              >{{ t(l.show_more) }}
              <template #append> <FontAwesomeIcon icon="fa-solid fa-chevron-down" class="ml-2" /> </template
            ></v-btn>
            <v-progress-circular
              v-if="!firstPageResults && isLoading(contextId)"
              class="ml-2"
              indeterminate
              size="30"
              color="primary"
              :aria-label="t(l.label_loading_search_results)"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

.area-guide-result {
  aspect-ratio: 16 / 9;
  background: radial-gradient(transparent, rgba(black, 0.1));
  text-align: center;
  position: relative;
  overflow: visible !important;

  h4 {
    color: rgba(var(--v-theme-on-primary));
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
  }

  .category-bubble {
    position: absolute;
    top: 10px;
    left: -10px;
  }

  &--subtitle {
    font-family: settings.$heading-font;
    font-size: 0.875rem;
    text-decoration-line: none !important;
    display: inline;
    border-bottom: 3px solid rgba(var(--v-theme-primary), 0.1);

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      font-size: 0.9375rem;
    }
  }
}

.search-results__container {
  min-height: 250px;
  .search-results-caption {
    color: rgba(var(--v-theme-secondary));
    font-size: 1.125rem;
    @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      font-size: 1.375rem;
    }
  }
}
</style>
